import * as React from 'react';
import { styled } from '@mui/material/styles';
import PropTypes from 'prop-types';
import { Button, CardContent, CircularProgress } from '@mui/material';
import {
    Form,
    required,
    useNotify,
    useSafeSetState,
} from 'ra-core';
import { TextInput } from 'react-admin';
import { PasswordInput } from 'react-admin';
import {
    useLocation,
    useNavigate
} from "react-router-dom";
import { addSentryBreadcrumb, captureRequestFailure } from '../monitoring/sentry';


function useQuery() {
    const { search } = useLocation();

    return React.useMemo(() => new URLSearchParams(search), [search]);
}


export const ChangePasswordForm = (props: LoginFormProps) => {
    const { className } = props;
    const navigate = useNavigate();
    const [loading, setLoading] = useSafeSetState(false);
    const notify = useNotify();
    let query = useQuery();
    let token = query.get('token');

    const submit = (values: FormData) => {
        const changePasswordUrl = `${process.env.REACT_APP_API_URL}/auth/change-password?token=${token}`;
        setLoading(true);
        addSentryBreadcrumb({
            category: 'auth',
            message: 'Change password requested',
            data: {
                endpoint: changePasswordUrl,
            },
        });
        fetch(changePasswordUrl, {
            method: 'POST',
            body: JSON.stringify(values),
            headers: {
                'Content-Type': 'application/json',
            }
        })
            .then((response) => {
                if (!response.ok) {
                    const error = new Error(response.statusText || 'Change password failed');
                    error.status = response.status;
                    throw error;
                }
                return response
            })
            .then((response) => {
                setLoading(false);
                addSentryBreadcrumb({
                    category: 'auth',
                    message: 'Password changed',
                    data: {
                        endpoint: changePasswordUrl,
                        status: response.status,
                    },
                });
                notify('Password has been changed', { type: 'success' });
                setTimeout(()=>{navigate("/")},5000);

            })
            .catch(error => {
                setLoading(false);
                if (!error.status || error.status >= 500) {
                    captureRequestFailure({
                        error,
                        operation: 'change_password',
                        url: changePasswordUrl,
                        method: 'POST',
                    });
                } else {
                    addSentryBreadcrumb({
                        category: 'auth',
                        message: 'Change password rejected',
                        level: 'warning',
                        data: {
                            endpoint: changePasswordUrl,
                            status: error.status,
                        },
                    });
                }
                notify(
                    typeof error === 'string'
                        ? error
                        : typeof error === 'undefined' || !error.message
                            ? 'ra.auth.sign_in_error'
                            : error.message,
                    {
                        type: 'warning',
                        messageArgs: {
                            _:
                                typeof error === 'string'
                                    ? error
                                    : error && error.message
                                        ? error.message
                                        : undefined,
                        },
                    }
                );
            });
    };

    return (
        <StyledForm
            onSubmit={submit}
            mode="onChange"
            noValidate
            className={className}
        >
            <CardContent className={LoginFormClasses.content}>
                <TextInput
                    autoFocus
                    source="email"
                    //label={translate('ra.auth.email')}
                    label='Email'
                    validate={required()}
                    fullWidth
                />
                <PasswordInput
                    source="password"
                    label="New password"
                    autoComplete="current-password"
                    validate={required()}
                    fullWidth
                />

                <Button
                    variant="contained"
                    type="submit"
                    color="primary"
                    disabled={loading}
                    fullWidth
                    className={LoginFormClasses.button}
                >
                    {loading ? (
                        <CircularProgress
                            className={LoginFormClasses.icon}
                            size={19}
                            thickness={3}
                        />
                    ) : (
                        //translate('ra.auth.sign_in')
                        'Send'
                    )}
                </Button>
            </CardContent>
        </StyledForm>
    );
};

const PREFIX = 'RaLoginForm';

export const LoginFormClasses = {
    content: `${PREFIX}-content`,
    button: `${PREFIX}-button`,
    icon: `${PREFIX}-icon`,
};

const StyledForm = styled(Form, {
    name: PREFIX,
    overridesResolver: (props, styles) => styles.root,
})(({ theme }) => ({
    [`& .${LoginFormClasses.content}`]: {
        width: 300,
    },
    [`& .${LoginFormClasses.button}`]: {
        marginTop: theme.spacing(2),
    },
    [`& .${LoginFormClasses.icon}`]: {
        margin: theme.spacing(0.3),
    },
}));

export interface LoginFormProps {
    redirectTo?: string;
    className?: string;
}

interface FormData {
    username: string;
    password: string;
}
ChangePasswordForm.propTypes = {
    redirectTo: PropTypes.string,
};
